import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getAddress, isAddress } from 'viem';
import { api } from '../lib/api';

const desiredChainId = Number(import.meta.env.VITE_CHAIN_ID ?? 97);

interface WalletState {
  address: string | null;
  chainId: number | null;
  connecting: boolean;
  authenticated: boolean;
  csrfToken: string | null;
  error: string | null;
  expectedChainId: number;
  connect(): Promise<void>;
  disconnect(): void;
  switchNetwork(): Promise<void>;
  authenticate(): Promise<void>;
}

const WalletContext = createContext<WalletState | null>(null);

const chainParameters = desiredChainId === 56
  ? {
      chainId: '0x38',
      chainName: 'BNB Smart Chain',
      nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
      rpcUrls: ['https://bsc-dataseed.bnbchain.org'],
      blockExplorerUrls: ['https://bscscan.com'],
    }
  : {
      chainId: '0x61',
      chainName: 'BNB Smart Chain Testnet',
      nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
      rpcUrls: [import.meta.env.VITE_RPC_PUBLIC_URL ?? 'https://data-seed-prebsc-1-s1.bnbchain.org:8545'],
      blockExplorerUrls: ['https://testnet.bscscan.com'],
    };

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sync = useCallback(async () => {
    if (!window.ethereum) return;
    const [accounts, chainHex] = await Promise.all([
      window.ethereum.request({ method: 'eth_accounts' }) as Promise<string[]>,
      window.ethereum.request({ method: 'eth_chainId' }) as Promise<string>,
    ]);
    const next = accounts[0];
    setAddress(next && isAddress(next) ? getAddress(next) : null);
    setChainId(Number.parseInt(chainHex, 16));
    if (!next) setAuthenticated(false);
  }, []);

  useEffect(() => {
    void sync();
    const handler = () => {
      setAuthenticated(false);
      setCsrfToken(null);
      void sync();
    };
    window.ethereum?.on?.('accountsChanged', handler);
    window.ethereum?.on?.('chainChanged', handler);
    return () => {
      window.ethereum?.removeListener?.('accountsChanged', handler);
      window.ethereum?.removeListener?.('chainChanged', handler);
    };
  }, [sync]);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setError('No injected wallet was found. Install a trusted EVM wallet to continue.');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      await sync();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Wallet connection was declined.');
    } finally {
      setConnecting(false);
    }
  }, [sync]);

  const disconnect = useCallback(() => {
    setAddress(null);
    setAuthenticated(false);
    setCsrfToken(null);
    setError(null);
  }, []);

  const switchNetwork = useCallback(async () => {
    if (!window.ethereum) return;
    setError(null);
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainParameters.chainId }],
      });
    } catch (cause) {
      const maybeCode = cause as { code?: number };
      if (maybeCode.code === 4902) {
        await window.ethereum.request({ method: 'wallet_addEthereumChain', params: [chainParameters] });
      } else {
        setError(cause instanceof Error ? cause.message : 'Network switch was declined.');
      }
    }
    await sync();
  }, [sync]);

  const authenticate = useCallback(async () => {
    if (!address || !window.ethereum || chainId !== desiredChainId) return;
    setConnecting(true);
    setError(null);
    try {
      const challenge = await api<{ nonce: string; message: string }>('/api/auth/challenge', {
        method: 'POST',
        body: JSON.stringify({ address, chainId }),
      });
      const signature = (await window.ethereum.request({
        method: 'personal_sign',
        params: [challenge.message, address],
      })) as string;
      const verified = await api<{ csrfToken: string }>('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ address, chainId, nonce: challenge.nonce, signature }),
      });
      setCsrfToken(verified.csrfToken);
      setAuthenticated(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Wallet authentication failed.');
    } finally {
      setConnecting(false);
    }
  }, [address, chainId]);

  const value = useMemo<WalletState>(
    () => ({
      address,
      chainId,
      connecting,
      authenticated,
      csrfToken,
      error,
      expectedChainId: desiredChainId,
      connect,
      disconnect,
      switchNetwork,
      authenticate,
    }),
    [address, chainId, connecting, authenticated, csrfToken, error, connect, disconnect, switchNetwork, authenticate],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletState {
  const value = useContext(WalletContext);
  if (!value) throw new Error('useWallet must be used inside WalletProvider');
  return value;
}
